'use client'

import { useId, useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import { motion, MotionConfig } from 'motion/react'

import { cn } from '@/utilities/ui'

/**
 * Utility to only trigger callback on keyboard focus (not mouse)
 */
function onlyKeyboardFocus(callback: () => void) {
  return (e: React.FocusEvent<HTMLButtonElement>) => {
    if (e.type === 'focus' && e.target.matches(':focus-visible')) {
      callback()
    }
  }
}

export interface AnimatedAccordionItemProps {
  header: string
  children: React.ReactNode
  defaultOpen?: boolean
  buttonClassName?: string
}

/**
 * Individual accordion item with animated expand/collapse
 */
export function AnimatedAccordionItem({
  header,
  children,
  defaultOpen = false,
  buttonClassName,
}: AnimatedAccordionItemProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [hasFocus, setHasFocus] = useState(false)
  const id = useId()

  return (
    <MotionConfig transition={{ duration: 0.3 }}>
      <motion.section
        initial={false}
        animate={isOpen ? 'open' : 'closed'}
        className="relative border-t border-border p-5"
      >
        <h3 className="m-0 flex">
          <motion.button
            id={id + '-button'}
            aria-expanded={isOpen}
            aria-controls={id}
            onClick={() => setIsOpen(!isOpen)}
            onFocus={onlyKeyboardFocus(() => setHasFocus(true))}
            onBlur={() => setHasFocus(false)}
            className={cn(
              'relative flex flex-1 cursor-pointer items-center justify-between rounded-sm border-none bg-transparent text-left font-body font-medium text-evergreen-400 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent',
              buttonClassName,
            )}
          >
            <span className="relative z-10 mr-4 min-w-0 text-left font-body text-foreground">
              {header}
            </span>
            <div className="relative flex shrink-0 items-center justify-center">
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                variants={{
                  open: { rotate: 180, opacity: 0 },
                  closed: { rotate: 0, opacity: 1 },
                }}
                transition={{ duration: 0.3 }}
              >
                <Plus className="size-6 shrink-0 pl-1" strokeWidth={2} />
              </motion.div>
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                variants={{
                  open: { rotate: 0, opacity: 1 },
                  closed: { rotate: -180, opacity: 0 },
                }}
                transition={{ duration: 0.3 }}
              >
                <Minus className="size-6 shrink-0" strokeWidth={2} />
              </motion.div>
            </div>
            {hasFocus && (
              <motion.div
                layoutId="focus-ring"
                className="absolute -inset-2.5 z-0 rounded bg-accent/10"
              />
            )}
          </motion.button>
        </h3>
        <motion.div
          variants={{
            open: {
              height: 'auto',
              maskImage: 'linear-gradient(to bottom, black 100%, transparent 100%)',
            },
            closed: {
              height: 0,
              maskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
            },
          }}
          id={id}
          aria-labelledby={id + '-button'}
          className="overflow-hidden"
        >
          <motion.div
            variants={{
              open: {
                filter: 'blur(0px)',
                opacity: 1,
              },
              closed: {
                filter: 'blur(2px)',
                opacity: 0,
              },
            }}
            className="pt-5"
          >
            {children}
          </motion.div>
        </motion.div>
        <hr className="absolute inset-x-5 bottom-0 z-0 m-0 border-0 border-b border-border last:hidden" />
      </motion.section>
    </MotionConfig>
  )
}

export interface AnimatedAccordionProps {
  children: React.ReactNode
  className?: string
}

/**
 * Animated accordion container using Motion for smooth expand/collapse animations
 */
export function AnimatedAccordion({ children, className }: AnimatedAccordionProps) {
  return (
    <div
      className={cn(
        'flex max-w-125 min-w-75 flex-col rounded-xl border border-border bg-card',
        '[&_section:last-child_hr]:hidden',
        '[&_p]:m-0 [&_p]:p-0 [&_p]:leading-relaxed',
        className,
      )}
    >
      {children}
    </div>
  )
}

export default AnimatedAccordion
